"use client";

import {
  type ComponentType,
  type CSSProperties,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import NextLink, { type LinkProps as NextLinkProps } from "next/link";
import { Link as LocaleLink } from "@/i18n/navigation";
import { headerNavLinkClass } from "@/app/components/header-nav-link";
import {
  PRIMARY_SERVICE_NAV_LINKS,
  type PrimaryServiceSubNavLink,
} from "@/app/components/primary-service-nav";
import type { Locale } from "@/lib/i18n/locales";
import { mandarinPrimaryServiceNav } from "@/lib/i18n/primary-service-nav-copy";
import { languageDestination } from "@/lib/i18n/navigation";
import { event as gaEvent } from "@/lib/gtag";

type NavLinkProps = Omit<NextLinkProps<string>, "href"> & { href: string };
const NativeNavLink = NextLink as ComponentType<NavLinkProps>;
const EnglishNavLink = LocaleLink as ComponentType<NavLinkProps>;

type PrimaryServiceNavItemsProps = {
  locale?: Locale;
  tone: "dark" | "light";
  action: "header" | "landing_header";
};

type DropdownPosition = {
  top: number;
  left: number;
  maxHeight: number;
};

const HOVER_OUT_CLOSE_DELAY_MS = 400;

function DisclosureIndicator({ isOpen }: { isOpen: boolean }) {
  return (
    <span
      data-nav-disclosure-indicator
      data-state={isOpen ? "open" : "closed"}
      className="relative ml-2 inline-flex h-3.5 w-3.5 shrink-0 items-center justify-center opacity-85"
      aria-hidden="true"
    >
      <span className="absolute h-px w-3 rounded-full bg-current" />
      <span
        className={`absolute h-3 w-px rounded-full bg-current transition-[transform,opacity] duration-200 motion-reduce:transition-none ${
          isOpen ? "scale-y-0 opacity-0" : "scale-y-100 opacity-100"
        }`}
      />
    </span>
  );
}

export default function PrimaryServiceNavItems({
  tone,
  action,
  locale = "en",
}: PrimaryServiceNavItemsProps) {
  const Link = locale === "zh-Hans" ? NativeNavLink : EnglishNavLink;
  const navItems =
    locale === "en"
      ? PRIMARY_SERVICE_NAV_LINKS
      : mandarinPrimaryServiceNav(PRIMARY_SERVICE_NAV_LINKS);
  const destination = (href: string) => primaryNavDestination(href, locale);
  const disclosure = (href: string) =>
    locale === "zh-Hans" && !destination(href).startsWith("/zh/")
      ? "（英文页面）"
      : "";
  const idPrefix = useId();
  const [openHref, setOpenHref] = useState<string | null>(null);
  const [dropdownPositions, setDropdownPositions] = useState<
    Record<string, DropdownPosition>
  >({});
  const handledPointerHref = useRef<string | null>(null);
  const mouseClickIntent = useRef<{ href: string; wasOpen: boolean } | null>(
    null
  );
  const closeTimerRef = useRef<number | null>(null);
  const triggerRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const panelRefs = useRef<Record<string, HTMLDivElement | null>>({});

  function clearCloseTimer() {
    if (closeTimerRef.current !== null) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }

  function scheduleHoverOutClose(href: string) {
    clearCloseTimer();
    closeTimerRef.current = window.setTimeout(() => {
      setOpenHref((current) => (current === href ? null : current));
      closeTimerRef.current = null;
    }, HOVER_OUT_CLOSE_DELAY_MS);
  }

  function closeIfOutside(target: EventTarget | null) {
    if (!(target instanceof Node)) {
      return;
    }

    setOpenHref((current) => {
      if (current == null) {
        return current;
      }

      const trigger = triggerRefs.current[current];
      const panel = panelRefs.current[current];
      const clickedInside =
        Boolean(trigger?.contains(target)) || Boolean(panel?.contains(target));

      return clickedInside ? current : null;
    });
  }

  useEffect(() => {
    function handlePointerDown(event: PointerEvent) {
      closeIfOutside(event.target);
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setOpenHref(null);
      }
    }

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
      clearCloseTimer();
    };
  }, []);

  function positionDropdown(
    href: string,
    trigger: HTMLElement,
    columnCount: number
  ) {
    const triggerRect = trigger.getBoundingClientRect();
    const panelWidth = Math.min(
      columnCount > 0 ? columnCount * 192 : 224,
      window.innerWidth - 32
    );
    const centeredLeft =
      triggerRect.left + triggerRect.width / 2 - panelWidth / 2;
    const left = Math.max(
      16,
      Math.min(centeredLeft, window.innerWidth - panelWidth - 16)
    );
    const viewport = window.visualViewport;
    const viewportBottom = viewport
      ? viewport.offsetTop + viewport.height
      : window.innerHeight;
    const maxHeight = Math.max(160, viewportBottom - triggerRect.bottom - 16);

    setDropdownPositions((current) => ({
      ...current,
      [href]: {
        left,
        top: triggerRect.bottom + 8,
        maxHeight,
      },
    }));
  }

  return (
    <>
      {navItems.map((item) => {
        const childLinks =
          item.children ?? item.childColumns?.flatMap((column) => column.links);
        const hasChildren = Boolean(childLinks?.length);
        const isOpen = openHref === item.href;
        const menuId = `${idPrefix}-${item.gaLabel}-subjects`;
        const columnCount = item.childColumns?.length ?? 0;
        const panelStyle =
          dropdownPositions[item.href] != null
            ? ({
                left: `${dropdownPositions[item.href].left}px`,
                top: `${dropdownPositions[item.href].top}px`,
                maxHeight: `${dropdownPositions[item.href].maxHeight}px`,
              } satisfies CSSProperties)
            : undefined;

        if (!hasChildren) {
          return (
            <Link
              key={item.href}
              href={destination(item.href)}
              prefetch={false}
              data-primary-nav-top="true"
              data-ga-event="primary_nav_click"
              data-ga-category="navigation"
              data-ga-action={action}
              data-ga-label={item.gaLabel}
              className={headerNavLinkClass({ tone })}
            >
              {item.label}
              {disclosure(item.href)}
            </Link>
          );
        }

        return (
          // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
          <div
            key={item.href}
            role="group"
            className={`group relative flex items-center ${
              isOpen ? "z-[200]" : "z-[80]"
            }`}
            onBlur={(event) => {
              if (!event.currentTarget.contains(event.relatedTarget)) {
                setOpenHref((current) =>
                  current === item.href ? null : current
                );
              }
            }}
            onPointerEnter={(event) => {
              if (event.pointerType === "mouse") {
                clearCloseTimer();
              }
            }}
            onPointerLeave={(event) => {
              if (event.pointerType === "mouse" && isOpen) {
                scheduleHoverOutClose(item.href);
              }
            }}
          >
            <div className="flex items-center">
              <button
                ref={(node) => {
                  triggerRefs.current[item.href] = node;
                }}
                type="button"
                aria-expanded={isOpen}
                aria-controls={menuId}
                aria-haspopup="true"
                data-primary-nav-top="true"
                data-ga-event="primary_nav_click"
                data-ga-category="navigation"
                data-ga-action={action}
                data-ga-label={item.gaLabel}
                className={headerNavLinkClass({
                  tone,
                  isOpen,
                })}
                onPointerEnter={(event) => {
                  if (event.pointerType === "mouse") {
                    clearCloseTimer();
                    positionDropdown(
                      item.href,
                      event.currentTarget,
                      columnCount
                    );
                    setOpenHref(item.href);
                    if (!isOpen) {
                      gaEvent("primary_nav_hover_open", {
                        category: "navigation",
                        action,
                        label: item.gaLabel,
                        source_surface: action,
                        interaction_method: "mouse_hover",
                      });
                    }
                  }
                }}
                onPointerDown={(event) => {
                  clearCloseTimer();
                  if (event.pointerType === "mouse") {
                    // Focus can open the menu before click. Toggle the state
                    // the person pressed, not the intervening focus state.
                    mouseClickIntent.current = {
                      href: item.href,
                      wasOpen: isOpen,
                    };
                    return;
                  }

                  mouseClickIntent.current = null;
                  event.preventDefault();
                  positionDropdown(item.href, event.currentTarget, columnCount);
                  handledPointerHref.current = item.href;
                  setOpenHref(isOpen ? null : item.href);
                }}
                onPointerCancel={() => {
                  mouseClickIntent.current = null;
                  handledPointerHref.current = null;
                }}
                onFocus={(event) => {
                  positionDropdown(item.href, event.currentTarget, columnCount);
                  setOpenHref(item.href);
                }}
                onClick={(event) => {
                  clearCloseTimer();
                  positionDropdown(item.href, event.currentTarget, columnCount);

                  if (handledPointerHref.current === item.href) {
                    handledPointerHref.current = null;
                    return;
                  }

                  const intent = mouseClickIntent.current;
                  mouseClickIntent.current = null;
                  setOpenHref((current) => {
                    const wasOpen =
                      intent?.href === item.href
                        ? intent.wasOpen
                        : current === item.href;
                    return wasOpen ? null : item.href;
                  });
                }}
              >
                {item.label}
                {disclosure(item.href)}
                <DisclosureIndicator isOpen={isOpen} />
              </button>
            </div>
            <div
              ref={(node) => {
                panelRefs.current[item.href] = node;
              }}
              id={menuId}
              className={dropdownPanelClass(isOpen, columnCount)}
              style={panelStyle}
              onPointerEnter={(event) => {
                if (event.pointerType === "mouse") {
                  clearCloseTimer();
                }
              }}
              onPointerLeave={(event) => {
                if (event.pointerType === "mouse") {
                  scheduleHoverOutClose(item.href);
                }
              }}
            >
              {item.childColumns ? (
                <div
                  className={`grid gap-2 ${
                    columnCount === 4
                      ? "grid-cols-2 sm:grid-cols-4"
                      : "grid-cols-1 sm:grid-cols-3"
                  }`}
                >
                  {item.childColumns.map((column) => (
                    <div
                      key={column.links.map((link) => link.href).join("|")}
                      className="min-w-0"
                    >
                      {column.heading ? (
                        <div className="px-3 pb-1 pt-2 text-sm font-extrabold text-slate-900">
                          {column.heading}
                        </div>
                      ) : null}
                      <ul className="space-y-1">
                        {column.links.map((child) => (
                          <li key={child.href}>
                            <DropdownLink
                              child={child}
                              locale={locale}
                              parentGaLabel={item.gaLabel}
                              action={action}
                              onClick={() => setOpenHref(null)}
                            />
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              ) : (
                <ul className="space-y-1">
                  {item.children?.map((child) => (
                    <li key={child.href}>
                      <DropdownLink
                        child={child}
                        locale={locale}
                        parentGaLabel={item.gaLabel}
                        action={action}
                        onClick={() => setOpenHref(null)}
                      />
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        );
      })}
    </>
  );
}

function DropdownLink({
  child,
  locale,
  parentGaLabel,
  action,
  onClick,
}: {
  child: PrimaryServiceSubNavLink;
  locale: Locale;
  parentGaLabel: string;
  action: PrimaryServiceNavItemsProps["action"];
  onClick: () => void;
}) {
  const Link = locale === "zh-Hans" ? NativeNavLink : EnglishNavLink;
  return (
    <Link
      href={primaryNavDestination(child.href, locale)}
      prefetch={false}
      aria-label={
        child.label +
        (locale === "zh-Hans" &&
        !primaryNavDestination(child.href, locale).startsWith("/zh/")
          ? "（英文页面）"
          : "")
      }
      data-ga-event="primary_nav_click"
      data-ga-category="navigation"
      data-ga-action={`${action}_dropdown`}
      data-ga-label={`${parentGaLabel}_${child.gaLabel}`}
      className="block rounded-lg px-3 py-2 text-sm font-semibold text-slate-700 transition hover:bg-blue-50 hover:text-blue-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500"
      onClick={onClick}
    >
      {child.shortLabel ?? child.label}
      {locale === "zh-Hans" &&
      !primaryNavDestination(child.href, locale).startsWith("/zh/")
        ? "（英文页面）"
        : ""}
    </Link>
  );
}

function dropdownPanelClass(isOpen: boolean, columnCount: number) {
  const widthClass =
    columnCount === 4 ? "w-[48rem]" : columnCount > 0 ? "w-[36rem]" : "w-56";

  return [
    isOpen ? "block" : "hidden",
    `fixed z-[300] ${widthClass} max-w-[calc(100vw-2rem)] overflow-y-auto overscroll-contain rounded-xl border border-slate-200 bg-white p-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] text-left shadow-xl`,
    "before:absolute before:-top-3 before:left-0 before:h-3 before:w-full before:content-['']",
  ].join(" ");
}

export function primaryNavDestination(href: string, locale: Locale): string {
  const [path, hash] = href.split("#");
  return (
    languageDestination(path, locale) + (hash === undefined ? "" : `#${hash}`)
  );
}
